(() => {
  'use strict';
  if (window.OTTBeatCommerce) return;

  const shopRoot = () => window.Shopify?.routes?.root || '/';
  const asId = (value) => String(value || '').replace(/[^0-9]/g, '');

  // V516: one hidden +50 EUR stems variant per eligible beat.
  // This keeps the beat stock unique while giving Digital Products an exact variant to fulfill.
  const STEMS_VARIANTS_BY_HANDLE = Object.freeze({
    'rentable':'58626835349768',
    'cendrier':'58627095331080',
    'au-coeur-des-femmes':'58627095363848',
    'la-loi-du-karma':'58627095396616',
    'le-renouveau-est-essentiel':'58627095429384',
    'anxiete-morbide':'58627095462152',
    'avenir-radieux':'58627095494920',
    'd-outre-tombe':'58627095527688',
    'faux-espoirs':'58627095560456',
    'incredible-dream':'58627095593224',
    'l-ombre-d-une-main':'58627095625992',
    'mariage-funeste':'58627095658760',
    'melody-maudite':'58627095691528',
    'mysterieuse-ile':'58627095724296',
    'perception':'58627095757064',
    'perplexe-cyber':'58627095789832',
    'puits-de-savoirs':'58627095822600',
    'sombres-reves':'58627095855368',
    'when-i-was-speed':'58627095888136',
    'you-to-emotional':'58627095920904',
    'marche-vers-la-reussite':'58627095953672',
    'marche-vers-le-nord':'58627095986440',
    'personne-dautres':'58627096019208',
    'rnb-guitare-melody':'58627096051976',
    'roulette-russe':'58627096084744',
    'suicide-collectif':'58627096117512',
    'takotsubo':'58627096150280',
    'amsterdam-high':'58627096183048',
    'au-cur-de-la-maladie':'58627096215816',
    'bronx':'58627096248584',
    'darkside':'58627096281352',
    'espoirs-enfuits':'58627096314120',
    'fidele-histoire':'58627096346888',
    'irreprochable':'58627096379656',
    'je-repensais-a-toi':'58627096412424',
    'la-mer-est-pas-loin':'58627096445192',
    'manipulation-douteuse':'58627096477960'
  });

  const normalizeHandle = (value) => String(value || '').trim().toLowerCase().replace(/^\/+|\/+$/g, '');
  const exactStemsVariant = (config = {}) => {
    const handle = normalizeHandle(config.productHandle);
    return handle && STEMS_VARIANTS_BY_HANDLE[handle] ? STEMS_VARIANTS_BY_HANDLE[handle] : '';
  };

  const getCart = async () => {
    const response = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart-read-failed');
    return response.json();
  };
  const updateByKeys = async (updates) => {
    const response = await fetch(`${shopRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) throw new Error('cart-update-failed');
    return response.json();
  };
  const addItems = async (items) => {
    const response = await fetch(`${shopRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const error = new Error(payload.description || payload.message || 'cart-add-failed');
      error.payload = payload;
      throw error;
    }
    return response.json();
  };

  const linkedAddons = (cart, baseVariantId) => cart.items.filter((item) => {
    const props = item.properties || {};
    return String(props._ott_addon || '') === 'stems' && String(props._ott_parent_variant_id || '') === String(baseVariantId);
  });

  const verifyFormulaCart = (cart, config, baseVariantId, addonVariantId, withStems) => {
    if (!cart || !Array.isArray(cart.items)) throw new Error('beat-cart-review-required');
    const bases = cart.items.filter((item) => String(item.variant_id) === String(baseVariantId));
    if (bases.length !== 1 || Number(bases[0].quantity) !== 1) throw new Error('beat-cart-review-required');
    if (config.productId && String(bases[0].product_id) !== String(config.productId)) {
      throw new Error('beat-cart-review-required');
    }
    const addons = linkedAddons(cart, baseVariantId);
    if (withStems) {
      if (addons.length !== 1) throw new Error('stems-cart-review-required');
      const addon = addons[0];
      const props = addon.properties || {};
      if (String(addon.variant_id) !== String(addonVariantId) ||
          Number(addon.quantity) !== 1 ||
          String(props._ott_stems_variant_id || '') !== String(addonVariantId) ||
          (config.productId && String(props._ott_parent_product_id || '') !== String(config.productId)) ||
          (config.productHandle && String(props._ott_parent_handle || '') !== String(config.productHandle))) {
        throw new Error('stems-cart-review-required');
      }
    } else if (addons.length) {
      throw new Error('stems-cart-review-required');
    }
    return cart;
  };

  const addFormula = async (config = {}) => {
    const baseVariantId = asId(config.baseVariantId);
    const withStems = Boolean(config.withStems);
    const mappedAddonVariantId = withStems ? exactStemsVariant(config) : '';
    const addonVariantId = asId(mappedAddonVariantId || config.addonVariantId);
    if (!baseVariantId) throw new Error('base-variant-missing');
    if (withStems && !mappedAddonVariantId) throw new Error('stems-beat-not-eligible');
    if (withStems && !addonVariantId) throw new Error('stems-addon-missing');

    const cart = await getCart();
    const baseLines = cart.items.filter((item) => String(item.variant_id) === baseVariantId);
    const baseLine = baseLines[0];
    const addons = linkedAddons(cart, baseVariantId);
    if (baseLines.length > 1 || (baseLine && Number(baseLine.quantity) !== 1)) {
      throw new Error('beat-cart-review-required');
    }

    if (baseLine) {
      if (withStems && !addons.length) {
        await addItems([{ quantity: 1, id: Number(addonVariantId), properties: {
          Instrumentale: config.beatTitle || 'Instrumentale OTTRecord',
          Option: 'Stems multipistes',
          _ott_addon: 'stems',
          _ott_parent_variant_id: baseVariantId,
          _ott_parent_product_id: String(config.productId || ''),
          _ott_parent_handle: String(config.productHandle || ''),
          _ott_parent_title: String(config.beatTitle || ''),
          _ott_stems_variant_id: addonVariantId
        }}]);
        return { mode: 'upgraded', cart: verifyFormulaCart(await getCart(), config, baseVariantId, addonVariantId, true), addonVariantId };
      }
      if (!withStems && addons.length) {
        const updates = {};
        addons.forEach((item) => { updates[item.key] = 0; });
        await updateByKeys(updates);
        return { mode: 'downgraded', cart: verifyFormulaCart(await getCart(), config, baseVariantId, addonVariantId, false) };
      }
      return { mode: 'already-in-cart', cart: verifyFormulaCart(cart, config, baseVariantId, addonVariantId, withStems), addonVariantId: addons[0]?.variant_id || addonVariantId };
    }

    const items = [{ quantity: 1, id: Number(baseVariantId), properties: {
      _ott_beat: 'true',
      _ott_stock_unit: '1',
      _ott_formula: withStems ? 'stems' : 'standard'
    }}];
    if (withStems) {
      items.push({ quantity: 1, id: Number(addonVariantId), properties: {
        Instrumentale: config.beatTitle || 'Instrumentale OTTRecord',
        Option: 'Stems multipistes',
        _ott_addon: 'stems',
        _ott_parent_variant_id: baseVariantId,
        _ott_parent_product_id: String(config.productId || ''),
        _ott_parent_handle: String(config.productHandle || ''),
        _ott_parent_title: String(config.beatTitle || ''),
        _ott_stems_variant_id: addonVariantId
      }});
    }
    await addItems(items);
    return { mode: withStems ? 'added-with-stems' : 'added-standard', cart: verifyFormulaCart(await getCart(), config, baseVariantId, addonVariantId, withStems), addonVariantId };
  };

  const removeBeatAndAddon = async (baseKey, baseVariantId) => {
    const cart = await getCart();
    const updates = {};
    if (baseKey) updates[baseKey] = 0;
    linkedAddons(cart, String(baseVariantId || '')).forEach((item) => { updates[item.key] = 0; });
    if (!Object.keys(updates).length) return cart;
    return updateByKeys(updates);
  };

  window.OTTBeatCommerce = { addFormula, getCart, updateByKeys, removeBeatAndAddon, stemsVariantForHandle: exactStemsVariant };
})();
